import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Plus, Pencil, Trash2, Users, UserPlus, UserMinus, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { PageHeader, EmptyState } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const emptyClient = { name: "", contact_person: "", email: "", phone: "", address: "", notes: "" };

export default function Clients() {
  const { t } = useI18n();
  const qc = useQueryClient();
  const { data = [], isLoading } = useQuery({ queryKey: ["clients"], queryFn: api.listClients });
  const { data: stats } = useQuery({
    queryKey: ["clients-stats"],
    queryFn: () => api.clientsStats(),
    refetchOnWindowFocus: false,
  });
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyClient);
  const [confirmId, setConfirmId] = useState(null);
  const [search, setSearch] = useState("");

  const createMut = useMutation({
    mutationFn: (body) => api.createClient(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["clients"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      setOpen(false);
      setForm(emptyClient);
      toast.success("Client created");
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Error"),
  });
  const updateMut = useMutation({
    mutationFn: ({ id, body }) => api.updateClient(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["clients"] });
      setOpen(false);
      setEditing(null);
      setForm(emptyClient);
      toast.success("Client updated");
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Error"),
  });
  const deleteMut = useMutation({
    mutationFn: (id) => api.deleteClient(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["clients"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      setConfirmId(null);
      toast.success("Deleted");
    },
  });

  function openCreate() {
    setEditing(null);
    setForm(emptyClient);
    setOpen(true);
  }
  function openEdit(c) {
    setEditing(c);
    setForm({ ...emptyClient, ...c });
    setOpen(true);
  }
  function submit(e) {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error(t("client_name") + " *");
      return;
    }
    if (editing) updateMut.mutate({ id: editing.id, body: form });
    else createMut.mutate(form);
  }

  const filtered = data.filter((c) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (
      (c.name || "").toLowerCase().includes(q) ||
      (c.contact_person || "").toLowerCase().includes(q) ||
      (c.email || "").toLowerCase().includes(q)
    );
  });

  return (
    <div data-testid="clients-page">
      <PageHeader
        overline={t("clients")}
        title={t("clients")}
        actions={
          <>
            <Input
              data-testid="clients-search"
              placeholder={t("search")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-64"
            />
            <Button data-testid="new-client-btn" onClick={openCreate} className="bg-primary hover:bg-primary/90">
              <Plus className="size-4 mr-1.5" /> {t("new_client")}
            </Button>
          </>
        }
      />

      {/* Client cohort summary */}
      {stats && (
        <div
          data-testid="clients-summary"
          className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5"
        >
          <ClientStatCard
            testid="clients-summary-current"
            label="Current clients"
            hint="active in last 90 days"
            tone="emerald"
            icon={Users}
            value={stats.current}
            total={stats.total}
          />
          <ClientStatCard
            testid="clients-summary-new"
            label="New clients"
            hint="added in last 30 days"
            tone="red"
            icon={UserPlus}
            value={stats.new}
            total={stats.total}
          />
          <ClientStatCard
            testid="clients-summary-old"
            label="Old clients"
            hint="no activity in 90 days"
            tone="slate"
            icon={UserMinus}
            value={stats.old}
            total={stats.total}
          />
        </div>
      )}

      {isLoading ? (
        <p className="text-muted-foreground">{t("loading")}</p>
      ) : filtered.length === 0 ? (
        <EmptyState
          title={t("no_data")}
          hint={t("new_client")}
          action={
            <Button data-testid="empty-new-client-btn" onClick={openCreate} className="bg-primary hover:bg-primary/90">
              <Plus className="size-4 mr-1.5" /> {t("new_client")}
            </Button>
          }
        />
      ) : (
        <div className="border border-border rounded-md bg-white overflow-hidden" data-testid="clients-table-wrap">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent border-b border-border">
                <TableHead className="overline">{t("client_name")}</TableHead>
                <TableHead className="overline">{t("contact_person")}</TableHead>
                <TableHead className="overline">{t("email")}</TableHead>
                <TableHead className="overline">{t("phone")}</TableHead>
                <TableHead className="overline">{t("address")}</TableHead>
                <TableHead className="overline text-right">{t("actions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((c) => (
                <TableRow key={c.id} data-testid={`client-row-${c.id}`}>
                  <TableCell className="font-medium">
                    <Link
                      to={`/clients/${c.id}`}
                      data-testid={`client-link-${c.id}`}
                      className="flex items-center gap-2 hover:text-primary"
                    >
                      <span className="size-7 grid place-items-center rounded-md bg-primary/10 text-primary">
                        <Users className="size-3.5" />
                      </span>
                      <span className="hover:underline">{c.name}</span>
                    </Link>
                  </TableCell>
                  <TableCell className="text-muted-foreground">{c.contact_person || "—"}</TableCell>
                  <TableCell className="text-muted-foreground mono text-xs">{c.email || "—"}</TableCell>
                  <TableCell className="text-muted-foreground mono text-xs">{c.phone || "—"}</TableCell>
                  <TableCell className="text-muted-foreground text-xs max-w-[240px] truncate">{c.address || "—"}</TableCell>
                  <TableCell className="text-right">
                    <Link to={`/clients/${c.id}`}>
                      <Button data-testid={`open-client-${c.id}`} variant="ghost" size="sm" className="text-primary">
                        <ArrowRight className="size-3.5" />
                      </Button>
                    </Link>
                    <Button data-testid={`edit-client-${c.id}`} variant="ghost" size="sm" onClick={() => openEdit(c)}>
                      <Pencil className="size-3.5" />
                    </Button>
                    <Button data-testid={`delete-client-${c.id}`} variant="ghost" size="sm" onClick={() => setConfirmId(c.id)} className="text-red-600 hover:text-red-700">
                      <Trash2 className="size-3.5" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="client-form-dialog" className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="font-display">
              {editing ? t("edit_client") : t("new_client")}
            </DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">
              Manage client contact details.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <Label className="overline">{t("client_name")} *</Label>
              <Input data-testid="client-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <Label className="overline">{t("contact_person")}</Label>
              <Input data-testid="client-form-contact" value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} />
            </div>
            <div>
              <Label className="overline">{t("email")}</Label>
              <Input data-testid="client-form-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div>
              <Label className="overline">{t("phone")}</Label>
              <Input data-testid="client-form-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </div>
            <div className="sm:col-span-2">
              <Label className="overline">{t("address")}</Label>
              <Textarea data-testid="client-form-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} rows={2} />
            </div>
            <div className="sm:col-span-2">
              <Label className="overline">{t("notes")}</Label>
              <Textarea data-testid="client-form-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} />
            </div>
            <DialogFooter className="sm:col-span-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)} data-testid="client-form-cancel">
                {t("cancel")}
              </Button>
              <Button type="submit" data-testid="client-form-save" className="bg-primary hover:bg-primary/90">
                {t("save")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmId} onOpenChange={(o) => !o && setConfirmId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("confirm_delete")}</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-delete-cancel">{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-ok"
              onClick={() => deleteMut.mutate(confirmId)}
              className="bg-red-600 hover:bg-red-700"
            >
              {t("delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}


function ClientStatCard({ testid, label, hint, tone, icon: Icon, value, total }) {
  const styles = {
    emerald: { card: "border-emerald-200 bg-emerald-50/50", num: "text-emerald-800", ring: "bg-emerald-100 text-emerald-700" },
    red:     { card: "border-red-200 bg-red-50/50",         num: "text-red-800",     ring: "bg-red-100 text-red-700" },
    slate:   { card: "border-slate-200 bg-white",           num: "text-slate-900",   ring: "bg-slate-100 text-slate-700" },
  };
  const s = styles[tone] || styles.slate;
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <div data-testid={testid} className={`rounded-md border p-4 ${s.card}`}>
      <div className="flex items-center justify-between mb-2">
        <div>
          <p className="overline">{label}</p>
          <p className="text-[10px] text-muted-foreground mt-0.5">{hint}</p>
        </div>
        <span className={`size-9 grid place-items-center rounded-full ${s.ring}`}>
          <Icon className="size-4" />
        </span>
      </div>
      <p className={`mono text-3xl font-bold ${s.num} leading-none`}>{value}</p>
      <div className="mt-2 flex items-center gap-2">
        <div className="h-1 flex-1 rounded-full bg-slate-100 overflow-hidden">
          <div
            className={`h-full ${tone === "emerald" ? "bg-emerald-500" : tone === "red" ? "bg-red-500" : "bg-slate-400"}`}
            style={{ width: `${pct}%` }}
          />
        </div>
        <span className="text-[10px] text-muted-foreground mono">{pct}%</span>
      </div>
    </div>
  );
}
